import { createContext, useContext, useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';

const SocketContext = createContext(null);

export function SocketProvider({ children }) {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);

  // Every session change hands AuthContext a new user object (sign-in, group switch,
  // starting or leaving « agir en tant que »), always after the new token is stored:
  // the old socket is closed and the new one dials with the token read right here.
  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token || !user) return;

    // Dev runs the API on its own port, so it needs an explicit URL. Every
    // built environment serves the app and the API from one origin behind
    // Nginx: an empty URL makes Socket.IO dial the page's own origin. Reading
    // VITE_SOCKET_URL in a build would bake in whatever the building machine
    // had in .env — which is how localhost:5433 reached production.
    const socketUrl = import.meta.env.DEV
      ? (import.meta.env.VITE_SOCKET_URL || 'http://localhost:5433')
      : '';

    const newSocket = io(socketUrl, {
      auth: { token },
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
      reconnectionAttempts: 10,
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <SocketContext.Provider value={socket}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
